import React, { useEffect, useMemo, useState } from "react";
import { useStyle } from "../../context/styleContext/styleContext";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Seo from "../../components/Seo";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

export default function UnsubscribeToMailingList() {
  const { style } = useStyle();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [email, setEmail] = useState("");
  const [pageIsLoading, setPageIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [status, setStatus] = useState({
    success: false,
    message: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    if (token) {
      unsubscribeWithToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const unsubscribeWithToken = async (unsubscribeToken) => {
    setPageIsLoading(true);
    setHasSubmitted(true);

    try {
      const { data } = await axios.get("/api/unsubscribe", {
        params: {
          token: unsubscribeToken,
        },
      });

      setStatus({
        success: !!data.success,
        message:
          data.message ||
          "You have been unsubscribed from Parish Audio marketing emails.",
      });

      if (data.success) {
        toast.success("You have been unsubscribed.");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "This unsubscribe link could not be processed. You can still unsubscribe manually below.";

      setStatus({
        success: false,
        message,
      });

      toast.error("Unable to unsubscribe with that link.");
    } finally {
      setPageIsLoading(false);
    }
  };

  const unsubscribeByEmail = async (e) => {
    e.preventDefault();

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      toast.error("Please enter your email address.");
      return;
    }

    setPageIsLoading(true);
    setHasSubmitted(true);

    try {
      const { data } = await axios.post("/api/marketing/unsubscribe", {
        email: trimmedEmail,
      });

      setStatus({
        success: !!data.success,
        message:
          data.message ||
          "If that email address is on the Parish Audio mailing list, it has been unsubscribed.",
      });

      if (data.success) {
        toast.success("Unsubscribe request processed.");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Something went wrong. Please try again or contact support.";

      setStatus({
        success: false,
        message,
      });

      toast.error("Unable to process unsubscribe request.");
    } finally {
      setPageIsLoading(false);
    }
  };

  const sharedTextFieldSx = {
    width: "100%",
    minWidth: 0,
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: "8px",
      minWidth: 0,
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-root": {
      minWidth: 0,
      "& fieldset": {
        borderColor: style.color2,
      },
      "&:hover fieldset": {
        borderColor: style.color3,
      },
      "&.Mui-focused fieldset": {
        borderColor: style.accentGreen,
      },
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      color: style.color3,
    },
    "input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0px 1000px ${style.color2} inset`,
      WebkitTextFillColor: style.color4,
      caretColor: style.color4,
      borderRadius: "8px",
    },
  };

  const showManualForm = !token || (hasSubmitted && !status.success);

  return (
    <>
      <Seo
        title="Unsubscribe | Parish Audio"
        description="Unsubscribe from Parish Audio marketing emails."
        canonical="https://parishaudio.com/unsubscribe-to-mailing-list"
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          color: style.color4,
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            maxWidth: 720,
            width: "100%",
            minWidth: 0,
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2.25, md: 3 },
                backgroundColor: style.darkPanel,
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack spacing={1} alignItems="flex-start">
                <Chip
                  icon={<EmailRoundedIcon />}
                  label="Email Preferences"
                  sx={{
                    borderRadius: "5px",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.06)",
                  }}
                />

                <Typography
                  sx={{
                    color: style.color4,
                    fontSize: { xs: "1.9rem", md: "2.5rem" },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                  }}
                >
                  Unsubscribe from marketing emails
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    maxWidth: 620,
                    lineHeight: 1.65,
                  }}
                >
                  You can unsubscribe from Parish Audio promotional emails here.
                  This will not affect important account, order, subscription, or
                  support messages.
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2, md: 3 },
              }}
            >
              {pageIsLoading ? (
                <LoadingSpinner />
              ) : (
                <Stack spacing={2.25}>
                  {hasSubmitted && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: "14px",
                        backgroundColor: status.success
                          ? "rgba(38,166,104,0.12)"
                          : "rgba(255,255,255,0.06)",
                        border: status.success
                          ? "1px solid rgba(38,166,104,0.35)"
                          : "1px solid rgba(255,255,255,0.10)",
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <MarkEmailReadRoundedIcon
                          sx={{
                            color: status.success
                              ? style.accentGreen
                              : style.color3,
                            mt: 0.2,
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 800,
                              fontSize: "1.05rem",
                              mb: 0.5,
                            }}
                          >
                            {status.success
                              ? "You have been unsubscribed"
                              : "We could not complete that automatically"}
                          </Typography>

                          <Typography
                            sx={{
                              color: style.color3,
                              lineHeight: 1.65,
                            }}
                          >
                            {status.message}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  {showManualForm && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: "14px",
                        backgroundColor: style.darkPanel,
                        border: "1px solid rgba(255,255,255,0.08)",
                        boxSizing: "border-box",
                        width: "100%",
                        minWidth: 0,
                        overflow: "hidden",
                      }}
                    >
                      <Box component="form" onSubmit={unsubscribeByEmail}>
                        <Stack spacing={1.5}>
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 800,
                              fontSize: "1.05rem",
                            }}
                          >
                            Enter your email address
                          </Typography>

                          <Typography
                            sx={{
                              color: style.color3,
                              lineHeight: 1.65,
                              fontSize: "0.95rem",
                            }}
                          >
                            Enter the email address you used to subscribe. If it
                            is on the Parish Audio mailing list, it will be
                            removed from future marketing emails.
                          </Typography>

                          <TextField
                            fullWidth
                            name="email"
                            label="Email address"
                            type="email"
                            size="small"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={sharedTextFieldSx}
                          />

                          <Button
                            type="submit"
                            variant="contained"
                            disabled={pageIsLoading}
                            startIcon={<EmailRoundedIcon />}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: style.accentGreen,
                              width: { xs: "100%", sm: "fit-content" },
                              minWidth: 0,
                              px: 2.25,
                              "&:hover": {
                                backgroundColor: style.accentGreen,
                                filter: "brightness(1.05)",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(255,255,255,0.45)",
                                backgroundColor: "rgba(255,255,255,0.08)",
                              },
                            }}
                          >
                            Unsubscribe
                          </Button>
                        </Stack>
                      </Box>
                    </Paper>
                  )}

                  <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Typography
                      sx={{
                        color: style.color3,
                        lineHeight: 1.6,
                        fontSize: "0.9rem",
                      }}
                    >
                      Changed your mind? You can opt back in from your account
                      preferences later.
                    </Typography>

                    <Button
                      variant="outlined"
                      onClick={() => navigate("/")}
                      startIcon={<ArrowBackRoundedIcon />}
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        borderColor: "rgba(255,255,255,0.12)",
                        width: { xs: "100%", sm: "auto" },
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.06)",
                          borderColor: "rgba(255,255,255,0.18)",
                        },
                      }}
                    >
                      Return Home
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}